/**
 * @file CreditLinksDialog.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-08
 * @modified 2026-10-14
 *
 * What a supplier credit relates to — its POs and bills — from the credit's
 * window (review, 2026-10-08). A REFERENCE only: never billing, never a PO's
 * status or supplied quantity, never a figure in Xero. So it can be changed on
 * any credit, entered here or keyed in Xero, in Xero or not; Xero's Reference
 * on the credit keeps what was sent with it.
 *
 * Starts from the credit's own references (`credit_links`, not the goods
 * links), lists billed POs too, and saves BOTH lists whole
 * (PUT /api/bills/:id/credit-links) — never a partial list that would wipe
 * the rest.
 *
 * 1.1.0 (2026-10-14): the bills come from CreditBillPicker, searched on the
 * server as the person types — it was the newest 100, loaded once, so an
 * older bill of a big supplier could not be named here either.
 */

import React, { useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import CreditBillPicker from './CreditBillPicker';
import BillPoPicker from './BillPoPicker';
import { useSlideOutButton } from './SlideOutButton';
import { formatDateShort } from '../utils/dateFormatters';
import { creditLinksPayload } from '../utils/supplierCredits';
import './SupplierCreditAllocationModal.css';

const labelOf = (b) => b?.supplier_reference || b?.bill_number || (b?.id ? `#${b.id}` : '');

function LinksBody({ credit, pos, bills, setPos, setBills, onAddPos, busy, error }) {
  useSlideOutButton([busy, pos.length, bills.length]);
  return (
    <div className="supplier-credit-allocation-body credit-links-body">
      <p className="sca-hint" role="note">
        <span className="material-symbols-rounded">info</span>
        A reference only: a credit never bills a PO, changes its status or a supplied quantity. Kept here — Xero&apos;s
        Reference on the credit keeps what was sent with it.
      </p>
      <div className="sca-section">
        <div className="sca-section-head">
          <h4>POs</h4>
          <div>
            <button type="button" className="btn-slideout" onClick={onAddPos} disabled={busy} title="The supplier's POs, billed ones too">
              <div className="icon-wrapper"><span className="material-symbols-rounded">playlist_add</span></div>
              <span className="btn-label">Add POs</span>
            </button>
          </div>
        </div>
        {pos.length ? (
          <ul className="credit-links-list">
            {pos.map(p => (
              <li key={`po-${p.id}`}>
                <strong>{p.purchase_order_number || `PO #${p.id}`}</strong>{p.status ? ` · ${p.status}` : ''}
                <button type="button" className="btn-icon" disabled={busy} title={`Take ${p.purchase_order_number || 'the PO'} off`}
                        onClick={() => setPos(prev => prev.filter(x => Number(x.id) !== Number(p.id)))}>
                  <span className="material-symbols-rounded">close</span>
                </button>
              </li>
            ))}
          </ul>
        ) : <p className="sca-empty">No PO named.</p>}
      </div>
      <div className="sca-section">
        <h4>Bills</h4>
        <CreditBillPicker
          accountId={credit?.account_id ? Number(credit.account_id) : null}
          xeroContactId={credit?.xero_contact_id || null}
          selected={bills}
          disabled={busy}
          placeholder="Add a related bill…"
          onAdd={(b) => setBills(prev => (prev.some(x => Number(x.id) === Number(b.id)) ? prev : [...prev, b]))}
        />
        {bills.length ? (
          <ul className="credit-links-list">
            {bills.map(b => (
              <li key={`bill-${b.id}`}>
                <strong>{labelOf(b)}</strong>{b.bill_date ? ` · ${formatDateShort(b.bill_date)}` : ''}
                <button type="button" className="btn-icon" disabled={busy} title="Take this bill off"
                        onClick={() => setBills(prev => prev.filter(x => Number(x.id) !== Number(b.id)))}>
                  <span className="material-symbols-rounded">close</span>
                </button>
              </li>
            ))}
          </ul>
        ) : <p className="sca-empty">No bill named.</p>}
      </div>
      {error && (
        <div className="sca-error" role="alert">
          <span className="material-symbols-rounded">error</span>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.credit — GET /api/bills/:id's bill (credit_links)
 * @param {() => void} props.onClose
 * @param {() => void} [props.onSaved]
 */
function CreditLinksDialog({ credit, onClose, onSaved }) {
  const [pos, setPos] = useState(() => [...(credit?.credit_links?.pos || [])]);
  const [bills, setBills] = useState(() => [...(credit?.credit_links?.bills || [])]);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/bills/${credit.id}/credit-links`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(creditLinksPayload(pos, bills))
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.success) { setError(d.error || 'The references were not saved.'); return; }
      onSaved && onSaved(d);
      onClose();
    } catch (err) {
      setError(`The references were not saved: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal
        isOpen
        onClose={busy ? () => {} : onClose}
        title={`What ${labelOf(credit) || 'this credit'} relates to`}
        size="lg"
        className="supplier-credit-allocation"
        footer={
          <div className="buttons slideout">
            <div>
              <button type="button" className="btn-slideout" data-read-safe onClick={onClose} disabled={busy}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                <span className="btn-label">Cancel</span>
              </button>
            </div>
            <div>
              <button type="button" className="btn-slideout btn-slideout-primary" data-write-action onClick={save} disabled={busy}>
                <div className="icon-wrapper">
                  <span className={`material-symbols-rounded${busy ? ' spinning' : ''}`}>{busy ? 'progress_activity' : 'save'}</span>
                </div>
                <span className="btn-label">{busy ? 'Saving…' : 'Save'}</span>
              </button>
            </div>
          </div>
        }
      >
        <LinksBody credit={credit} pos={pos} bills={bills} setPos={setPos} setBills={setBills}
          onAddPos={() => setPicking(true)} busy={busy} error={error} />
      </Modal>
      <BillPoPicker
        isOpen={picking}
        supplierId={credit?.account_id ? Number(credit.account_id) : null}
        xeroContactId={credit?.xero_contact_id || null}
        supplierName={credit?.supplier_name || credit?.account_company_name || ''}
        linkedIds={pos.map(p => Number(p.id))}
        title="POs this credit relates to"
        addLabel="Relate"
        initialIncludeBilled
        onClose={() => setPicking(false)}
        onAdd={(rows) => {
          setPicking(false);
          setPos(prev => [...prev, ...rows.filter(r => !prev.some(p => Number(p.id) === Number(r.id))).map(r => ({
            id: Number(r.id), purchase_order_number: r.purchase_order_number, xero_purchase_order_id: r.xero_purchase_order_id || null, status: r.status
          }))]);
        }}
      />
    </>
  );
}

export default CreditLinksDialog;
